// DS: C25B
import React, {
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
  AriaAttributes,
} from 'react';
import classnames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import { PopperDropdownManager } from '../../PopperDropdownLayout';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { IconSize } from '../../../Icons/IconWrap';
import {
  NullableDropdownItem,
  DropdownOption,
  DropdownItem,
} from '../../types';
import { Props as PopperDropdownManagerProps } from '../../PopperDropdownLayout/PopperDropdownManager';
import StandardDropdownToggle, {
  Props as StandardDropdownToggleProps,
} from './StandardDropdownToggle';
import StandardDropdownMenu, {
  StandardDropdownMenuProps,
} from './StandardDropdownMenu';
import {
  isDropdownGroup,
  getItemValue,
  getSelectedItemByValue,
  findItemByValue,
} from '../../helpers';
import { useDropdownItems } from '../../hooks/useDropdownItems';
import type { PopperPlacement } from '../../../popper/types';

import s from './StandardDropdown.scss';

export interface Props
  extends Pick<PopperDropdownManagerProps, 'isInPopup'>,
    Pick<StandardDropdownToggleProps, 'onOpenBiData' | 'onCloseBiData'>,
    Pick<StandardDropdownMenuProps, 'role'> {
  items: NullableDropdownItem[];
  disabled?: boolean;
  initiallyOpen?: boolean;
  menuLabel?: React.ReactNode;
  usePortal?: boolean;
  className?: string;
  arrowSize?: IconSize;
  arrowClassName?: string;
  placeholder?: React.ReactNode;
  placeholderClassName?: string;
  placement?: PopperPlacement;
  selectedValue?: string | number;
  itemClassName?: string;
  openClassName?: string;
  menuClassName?: string;
  popperClassName?: string;
  menuScrollable?: boolean;
  toggleClassName?: string;
  withHoverEffect?: boolean;
  withScrollableArrows?: boolean;
  withMenuPaddings?: boolean; // menu paddings
  hideCheckIcon?: boolean;
  menuLabelClassName?: string;
  'aria-label'?: AriaAttributes['aria-label'];
  tabIndex?: number;
  biData?: BiEvent;
  onSelect?: (value, label?) => void;
  renderArrowDown?: (className: string) => React.ReactNode;
  children?: React.ReactNode;
}

function getItemsWithSelectedProp(
  selectedItem: DropdownOption,
  items: DropdownItem[]
) {
  return items.map((item: DropdownItem) => {
    if (isDropdownGroup(item)) {
      return item;
    }
    const isSelected = getItemValue(item) === getItemValue(selectedItem);
    if (isSelected !== item.selected) {
      return {
        ...item,
        selected: isSelected,
      };
    }
    return item;
  });
}

const StandardDropdown: React.FC<React.PropsWithChildren<Props>> = ({
  disabled,
  renderArrowDown,
  placeholder,
  placeholderClassName,
  arrowClassName,
  arrowSize = IconSize.SMALL,
  toggleClassName,
  openClassName,
  usePortal,
  placement = 'bottom-start',
  menuClassName,
  popperClassName,
  menuScrollable,
  menuLabel,
  menuLabelClassName,
  items,
  itemClassName,
  hideCheckIcon,
  biData,
  onOpenBiData = biData,
  onCloseBiData = biData,
  withHoverEffect,
  withScrollableArrows,
  withMenuPaddings,
  onSelect,
  className,
  selectedValue,
  role,
  'aria-label': ariaLabel,
  isInPopup,
  tabIndex,
  initiallyOpen,
}) => {
  const { filteredItems } = useDropdownItems(items);
  const [selectedItem, setSelectedItem] = useState<DropdownOption | undefined>(
    findItemByValue(selectedValue, filteredItems)
  );
  const { current: dropdownId } = useRef(uniqueId('dropdown'));

  const itemsWithSelectedProp = useMemo(() => {
    if (!selectedItem) {
      return filteredItems;
    }
    return getItemsWithSelectedProp(selectedItem, filteredItems);
  }, [filteredItems, selectedItem]);

  const onMenuSelect = useCallback(
    (value: string | number | undefined, label: any) => {
      const selected = getSelectedItemByValue(value, filteredItems);
      setSelectedItem(selected);
      onSelect && onSelect(value, label);
    },
    [filteredItems, onSelect]
  );

  // Update selected state if it's undefined and `selectedValue` prop changes
  useEffect(() => {
    if (!selectedItem && selectedValue !== undefined) {
      setSelectedItem(findItemByValue(selectedValue, filteredItems));
    }
  }, [selectedItem, selectedValue, filteredItems]);

  // Update selected state if item in list got a new label to avoid a stale toggle title
  useEffect(() => {
    const itemInList =
      selectedItem && findItemByValue(selectedItem.value, filteredItems);
    const labelChanged = itemInList && selectedItem.label !== itemInList.label;
    if (labelChanged) {
      setSelectedItem(itemInList);
    }
  }, [selectedItem, filteredItems]);

  return (
    <div className={classnames(s['root'], className)}>
      <PopperDropdownManager disabled={disabled} isInPopup={isInPopup}>
        <StandardDropdownToggle
          dropdownId={dropdownId}
          selectedItem={selectedItem}
          renderArrowDown={renderArrowDown}
          placeholder={placeholder}
          placeholderClassName={placeholderClassName}
          arrowSize={arrowSize}
          arrowClassName={arrowClassName}
          toggleClassName={toggleClassName}
          openClassName={openClassName}
          onOpenBiData={onOpenBiData}
          onCloseBiData={onCloseBiData}
          aria-label={ariaLabel}
          aria-haspopup={role}
          tabIndex={tabIndex}
        />
        <StandardDropdownMenu
          id={dropdownId}
          role={role}
          items={itemsWithSelectedProp}
          usePortal={usePortal}
          menuLabel={menuLabel}
          placement={placement}
          initiallyOpen={initiallyOpen}
          className={menuClassName}
          popperClassName={popperClassName}
          itemClassName={itemClassName}
          menuScrollable={menuScrollable}
          hideCheckIcon={hideCheckIcon}
          menuLabelClassName={menuLabelClassName}
          withHoverEffect={withHoverEffect}
          withMenuPaddings={withMenuPaddings}
          withScrollableArrows={withScrollableArrows}
          onSelect={onMenuSelect}
        />
      </PopperDropdownManager>
    </div>
  );
};
StandardDropdown.displayName = 'StandardDropdown';

export default StandardDropdown;
